import { LanguagePicker } from '@/components/language-picker';
import { getMenuItems, getMyAccount } from '@/constants/nav';
import { useLanguagePickerVisibility } from '@/hooks/use-language-picker-visibility';
import { useUrl } from '@/hooks/use-url';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';

export default function Top({ site, page, show_login_only }) {
  const locale = useLocale();
  const router = useRouter();
  const menuItems = getMenuItems(locale);
  const myAccount = getMyAccount(locale);

  const t = useTranslations('Navigation');
  const { getAppUrl } = useUrl();

  const getCurrentPath = () => {
    return page?.path || router.asPath || '/';
  };

  const showLanguagePicker = useLanguagePickerVisibility(getCurrentPath());
  const [showMenu, setShowMenu] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    setShowMenu(!isMobile);
  }, []);

  return (
    <div id="top" className={showMenu ? 'active' : undefined} data-template={page ? page.template : null}>
      <div className="top-inner">
        <div className="logo">
          <Link href="/">
            <Image src="/images/logo/makemycv.svg" alt="MakeMyCV" width={139} height={26} loading="lazy" />
          </Link>
        </div>

        {!show_login_only ? (
          <>
            <div className="absolute right-5 top-5 flex items-center gap-1 md:right-[40px] md:top-[30px] lg:hidden">
              {showLanguagePicker ? <LanguagePicker size="xs" /> : null}
              <button
                className="flex h-8 w-8 items-center justify-center rounded-md border border-input"
                onClick={() => setShowMenu(!showMenu)}
              >
                <Image src="/assets/icons/menu.svg" alt="menu" width={10} height={10} loading="lazy" />
              </button>
            </div>

            <div className={'menus' + (showMenu ? ' visible' : '')}>
              <div className="menu">
                <div className="menu-inner">
                  <ul>
                    {menuItems.map((item, i) => {
                      return (
                        <li key={item.title} className={'lv1' + (activeIndex === i ? ' active' : '')}>
                          <span
                            className={'lv1-title' + (activeIndex === i ? ' active' : '')}
                            onClick={() => setActiveIndex(i === activeIndex ? -1 : i)}
                          >
                            {item.titleKey ? t(item.titleKey) : item.title}
                          </span>
                          {item.children?.length > 0 && (
                            <ul
                              className={
                                'lv1-children' +
                                (activeIndex === i ? ' active' : '') +
                                (item.children.length >= 6 ? ' large' : '')
                              }
                            >
                              {item.children.map((child) => {
                                if (['button', 'link'].includes(child.type)) {
                                  return (
                                    <li className={'lv2 ' + child.type} key={child.title}>
                                      {child.image && child.image.url && (
                                        <img
                                          alt={child.title}
                                          className="icon"
                                          src={child.image.url}
                                          width="32"
                                          height="32"
                                          loading="lazy"
                                        />
                                      )}
                                      {child.url && child.url.indexOf('/create') > -1 && (
                                        <Link
                                          href={
                                            getAppUrl('create/' + child.url.split('/create/')[1]) +
                                            '?ref=' +
                                            getCurrentPath()
                                          }
                                          className="a"
                                        >
                                          <span className={`${child.type}-title`}>
                                            {child.titleKey ? t(child.titleKey) : child.title}
                                          </span>
                                          {child.type == 'button' && (
                                            <img
                                              src="/assets/icons/arrow-right-white.svg"
                                              alt="arrow right"
                                              width="16"
                                              height="16"
                                              loading="lazy"
                                            />
                                          )}
                                        </Link>
                                      )}
                                      {child.url && child.url.indexOf('/create') == -1 && (
                                        <Link href={child.url}>
                                          <span className={`${child.type}-title`}>
                                            {child.titleKey ? t(child.titleKey) : child.title}
                                          </span>
                                          {child.type == 'button' && (
                                            <img
                                              src="/assets/icons/arrow-right-white.svg"
                                              alt="arrow right"
                                              width="16"
                                              height="16"
                                              loading="lazy"
                                            />
                                          )}
                                        </Link>
                                      )}
                                      {child.children && child.children.length > 0 && (
                                        <ul>
                                          {child.children.map((subchild) => {
                                            return (
                                              <li className={subchild.type} key={subchild.title}>
                                                {subchild.image && subchild.image.url && (
                                                  <img
                                                    src={subchild.image.url}
                                                    alt={subchild.title}
                                                    className="icon"
                                                    width="32"
                                                    height="32"
                                                    loading="lazy"
                                                  />
                                                )}
                                                <Link
                                                  href={
                                                    subchild.url && subchild.url.indexOf('/create') > -1
                                                      ? getAppUrl('create/' + subchild.url.split('/create/')[1]) +
                                                        '?ref=' +
                                                        getCurrentPath()
                                                      : subchild.url || '/'
                                                  }
                                                >
                                                  <span className={`${subchild.type}-title`}>
                                                    {subchild.titleKey ? t(subchild.titleKey) : subchild.title}
                                                  </span>
                                                </Link>
                                              </li>
                                            );
                                          })}
                                        </ul>
                                      )}
                                    </li>
                                  );
                                }
                                if (child.type === 'group' || child.type === 'group-link') {
                                  return (
                                    <li className={'lv2 group'} key={child.title}>
                                      {child.image && child.image.url && (
                                        <img
                                          src={child.image.url}
                                          alt={child.title}
                                          className="icon"
                                          width="32"
                                          height="32"
                                          loading="lazy"
                                        />
                                      )}
                                      {child.type === 'group' && (
                                        <span className={`group-title`}>
                                          {child.titleKey ? t(child.titleKey) : child.title}
                                        </span>
                                      )}
                                      {child.type === 'group-link' && (
                                        <span className={`group-title`}>
                                          <Link href={child.url}>
                                            {child.titleKey ? t(child.titleKey) : child.title}
                                          </Link>
                                        </span>
                                      )}
                                      {child.type === 'button' && (
                                        <img
                                          src="/assets/icons/arrow-right-white.svg"
                                          alt="arrow right"
                                          width="16"
                                          height="16"
                                          loading="lazy"
                                        />
                                      )}
                                      {child.children?.length > 0 && (
                                        <ul>
                                          {child.children.map((subchild) => {
                                            return (
                                              <li className={subchild.type} key={subchild.title}>
                                                {subchild.image && subchild.image.url && (
                                                  <img
                                                    src={subchild.image.url}
                                                    alt={subchild.title}
                                                    className="icon"
                                                    width="32"
                                                    height="32"
                                                    loading="lazy"
                                                  />
                                                )}
                                                <Link
                                                  href={
                                                    subchild.url && subchild.url.indexOf('/create') > -1
                                                      ? getAppUrl('create/' + subchild.url.split('/create/')[1]) +
                                                        '?ref=' +
                                                        getCurrentPath()
                                                      : subchild.url || '/'
                                                  }
                                                >
                                                  <span className={`${subchild.type}-title`}>
                                                    {subchild.titleKey ? t(subchild.titleKey) : subchild.title}
                                                  </span>
                                                </Link>
                                              </li>
                                            );
                                          })}
                                        </ul>
                                      )}
                                    </li>
                                  );
                                }
                              })}
                            </ul>
                          )}
                          <div
                            className={'lv1-trigger' + (activeIndex === i ? ' active' : '')}
                            onClick={() => setActiveIndex(i === activeIndex ? -1 : i)}
                          >
                            <img
                              src="/assets/icons/caret-up-purple.svg"
                              alt="caret up"
                              width="24"
                              height="24"
                              loading="lazy"
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {showLanguagePicker && (
                <div className="hidden lg:block">
                  <LanguagePicker />
                </div>
              )}

              <div className="sign-menu">
                <ul>
                  <li>
                    <Link href={getAppUrl(myAccount.url)}>{t(myAccount.titleKey)}</Link>
                  </li>
                </ul>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-end space-x-2">
            {showLanguagePicker && <LanguagePicker />}
            <Link href={getAppUrl(myAccount.url)}>
              <button
                type="button"
                className="absolute right-5 top-5 flex h-[32px] items-center justify-center rounded-md border border-[#dadee9] px-3 text-[13px] font-semibold lg:hidden"
              >
                {t(myAccount.titleKey)}
              </button>
            </Link>
            <Link href={getAppUrl(myAccount.url)}>
              <button
                type="button"
                className="hidden h-[44px] items-center rounded-md bg-[#ecf1fd] px-4 text-indigo-600 lg:flex"
              >
                {t(myAccount.titleKey)}
              </button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
